import {RouteProp, useRoute} from '@react-navigation/native';
import React, {useMemo} from 'react';
import {ScrollView, TouchableOpacity, useWindowDimensions} from 'react-native';

import Button from '@/components/Button';
import Card from '@/components/Card/Card';
import DynamicImage from '@/components/DynamicImage/DynamicImage';
import PlayerControls from '@/components/PlayerControls';
import PlayerProgress from '@/components/PlayerProgress/PlayerProgress';
import Screen from '@/components/Screen/Screen';
import ScreenError from '@/components/ScreenError';
import ScreenLoader from '@/components/ScreenLoader';
import Space from '@/components/Space/Space';
import Text from '@/components/Text/Text';
import {useEmbedColorsListener} from '@/hooks/useEmbedColorsListener';
import {useEmbedPlayer} from '@/hooks/useEmbedPlayer';
import {useCustomThemesConfigQuery} from '@/queries/themes';
import {useEmbedTrackQuery} from '@/queries/tracks';
import {useCustomColors, useThemedStyles} from '@/theme';
import {useOverrideTheme} from '@/theme/useOverrideTheme';
import {ImageSize} from '@/types/media';
import {EmbedStackParams, Routes} from '@/types/routes';
import {getArtistUrl, getTrackUrl} from '@/utils/share';

import {styles} from './Player.style';

const HORIZONTAL_LAYOUT_BREAKPOINT = 279;
const MINIMIZED_CONTROLS_BREAKPOINT = 320;

const EmbedPlayer = () => {
  const style = useThemedStyles(styles);
  const {height: screenHeight} = useWindowDimensions();
  const horizontalLayout = screenHeight < HORIZONTAL_LAYOUT_BREAKPOINT;

  const {
    params: {id, colors, theme, referral},
  } = useRoute<RouteProp<EmbedStackParams, Routes.EmbedPlayer>>();
  const {
    trackDetails: track,
    query: {isLoading: embedTrackQueryIsLoading},
  } = useEmbedTrackQuery(id);

  const tracks = useMemo(() => (track ? [track] : []), [track]);
  const {
    currentTrack,
    isPlaying,
    isBuffering,
    togglePlay,
    hasNext,
    next,
    previous,
    seekTo,
  } = useEmbedPlayer(tracks);

  const {
    artistThemes,
    query: {isLoading: customThemesConfigIsLoading},
  } = useCustomThemesConfigQuery(true);

  const isLoading = embedTrackQueryIsLoading || customThemesConfigIsLoading;

  useOverrideTheme(
    // prioritize theme from url param
    theme
      ? {predefinedThemeName: theme}
      : {
          predefinedThemeName:
            track?.artist &&
            (track.artist.predefinedThemeName || artistThemes[track.artist.id]),
          customTheme: track?.artist.customTheme,
        },
  );

  useCustomColors(colors);
  useEmbedColorsListener();

  const onTrackTitlePress = () => {
    if (track) {
      window.open(getTrackUrl(track, referral));
    }
  };

  const onArtistPress = () => {
    if (track) {
      window.open(getArtistUrl(track.artist.slug));
    }
  };

  if (isLoading) {
    return <ScreenLoader />;
  }

  if (!track) {
    return <ScreenError textId="notFound" />;
  }

  return (
    <Screen>
      <ScrollView
        showsVerticalScrollIndicator={false}
        bounces={false}
        contentContainerStyle={style.scrollViewContainer}>
        <Space p="s" flex>
          <Space style={style.centered}>
            <TouchableOpacity onPress={onTrackTitlePress}>
              <Text size="xs" id="embed.listen" />
            </TouchableOpacity>
          </Space>
          {horizontalLayout ? (
            <Space pt="s" pb="xs" style={style.horizontalContainer} flex>
              <Space style={style.horizontalArtworkCardWrapper}>
                <Card style={style.coverArt}>
                  {track.lossyArtworkUrl && (
                    <DynamicImage
                      resizeWidth={ImageSize.player}
                      source={{uri: track.lossyArtworkUrl}}
                      style={style.coverArt}
                      hasBorderRadius
                    />
                  )}
                </Card>
              </Space>
              <Space w="s" />
              <Space flex>
                <TouchableOpacity onPress={onTrackTitlePress}>
                  <Text size="s" weight="semibold" numberOfLines={1}>
                    {track.title}
                  </Text>
                </TouchableOpacity>
                <Space h="xxs" />
                <TouchableOpacity onPress={onArtistPress}>
                  <Text size="xs" numberOfLines={1}>
                    {track.artist.name}
                  </Text>
                </TouchableOpacity>
              </Space>
            </Space>
          ) : (
            <>
              <Space style={style.verticalArtworkCardWrapper} mv="s">
                <Card style={style.verticalArtworkCard}>
                  {track.lossyArtworkUrl && (
                    <DynamicImage
                      resizeWidth={ImageSize.player}
                      source={{uri: track.lossyArtworkUrl}}
                      style={style.coverArt}
                      hasBorderRadius
                    />
                  )}
                </Card>
              </Space>
              <Space style={style.centered}>
                <TouchableOpacity onPress={onTrackTitlePress}>
                  <Text size="m" weight="semibold">
                    {track.title}
                  </Text>
                </TouchableOpacity>
                <Space mt="xxs" />
                <TouchableOpacity onPress={onArtistPress}>
                  <Text>{track.artist.name}</Text>
                </TouchableOpacity>
              </Space>
            </>
          )}
          <Space style={style.playerProgressRow}>
            <Space flex>
              <PlayerProgress
                onProgressChange={seekTo}
                isLoading={isBuffering}
              />
            </Space>
            {horizontalLayout && (
              <Button
                onPress={togglePlay}
                containerStyle={style.playButton}
                icon={{
                  name: isPlaying ? 'control-pause' : 'control-play',
                  size: 14,
                }}
              />
            )}
          </Space>
          {!horizontalLayout && (
            <Space mt="xs">
              <PlayerControls
                onPlay={togglePlay}
                onNext={next}
                onPrevious={previous}
                isPlaying={isPlaying}
                hasPrevious={!!currentTrack}
                hasNext={hasNext}
                iconSize={18}
                controlStyle={
                  screenHeight < MINIMIZED_CONTROLS_BREAKPOINT &&
                  style.controlSmall
                }
              />
            </Space>
          )}
        </Space>
      </ScrollView>
    </Screen>
  );
};

export default EmbedPlayer;
